/*
 * filterMapping — helpers for reconciling the filter names used in an imported
 * CSV target list (which follow the *export source's* convention) with the
 * filters actually installed on the *target observatory*.
 *
 *  - describeFilter(source, code): the human-readable "real" filter name for a
 *    source code (e.g. AAVSO "R" → "Cousins R"), used for the hover tooltip.
 *  - proposeTargetFilter(code, targetOptions): the best-guess observatory
 *    filter to map a source code onto, used as the pulldown default.
 */

// Imaging / narrowband conventions shared by most sources (ACP, SIMBAD, generic).
const GENERIC_FILTER_NAMES = {
  L: 'Luminance', LUM: 'Luminance', LUMINANCE: 'Luminance',
  R: 'Red', G: 'Green', B: 'Blue',
  HA: 'Hydrogen-alpha (Hα)', HALPHA: 'Hydrogen-alpha (Hα)', 'H-ALPHA': 'Hydrogen-alpha (Hα)',
  OIII: 'Oxygen-III (OIII)', O3: 'Oxygen-III (OIII)',
  SII: 'Sulfur-II (SII)', S2: 'Sulfur-II (SII)',
  C: 'Clear', CLEAR: 'Clear', OPEN: 'Open', L_PRO: 'Luminance (light pollution)',
  V: 'Visual', I: 'Infrared', U: 'Ultraviolet',
  SU: "Sloan u′", SG: "Sloan g′", SR: "Sloan r′", SI: "Sloan i′", SZ: "Sloan z′",
  CBB: 'Clear Blue-Blocking',
};

// AAVSO / exoplanet photometry: single letters are Johnson-Cousins standard
// bands, and there are clear/tri-color/Sloan variants specific to photometry.
const PHOTOMETRIC_FILTER_NAMES = {
  U: 'Johnson U', B: 'Johnson B', V: 'Johnson V',
  R: 'Cousins R', I: 'Cousins I',
  SU: "Sloan u′", SG: "Sloan g′", SR: "Sloan r′", SI: "Sloan i′", SZ: "Sloan z′",
  TB: 'Tri-color Blue', TG: 'Tri-color Green', TR: 'Tri-color Red',
  CV: 'Clear (V-band zero point)', CR: 'Clear (R-band zero point)',
  HA: 'Hydrogen-alpha', HB: 'Hydrogen-beta',
};

const NAMES_BY_SOURCE = {
  aavso: PHOTOMETRIC_FILTER_NAMES,
  exoplanet: PHOTOMETRIC_FILTER_NAMES,
  acp: GENERIC_FILTER_NAMES,
  simbad: GENERIC_FILTER_NAMES,
  generic: GENERIC_FILTER_NAMES,
};

const normalize = (s) => String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Human-readable name for a source filter code (for the hover tooltip).
 * Falls back to the generic table and finally the raw code.
 */
export function describeFilter(source, code) {
  if (!code) return '';
  const key = normalize(code);
  const sourceMap = NAMES_BY_SOURCE[source] || GENERIC_FILTER_NAMES;
  return sourceMap[key] || GENERIC_FILTER_NAMES[key] || String(code);
}

// Groups of codes that refer to the same physical filter across naming schemes.
const ALIAS_GROUPS = [
  ['L', 'LUM', 'LUMINANCE'],
  ['HA', 'HALPHA', 'HYDROGENALPHA'],
  ['OIII', 'O3', 'OXYGENIII'],
  ['SII', 'S2', 'SULFURII'],
  ['C', 'CLEAR', 'OPEN', 'CV', 'CR'],
  ['SR', 'SLOANR', 'RPRIME'],
  ['SG', 'SLOANG', 'GPRIME'],
  ['SI', 'SLOANI', 'IPRIME'],
  ['SU', 'SLOANU', 'UPRIME'],
  ['SZ', 'SLOANZ', 'ZPRIME'],
];

/**
 * Best-guess observatory filter (a value from targetOptions) for a source code.
 * Returns '' when there is no confident match, so the caller can require the
 * user to pick explicitly.
 *
 * @param {string} code - source filter code from the CSV
 * @param {Array<{value: string, label?: string}>} targetOptions - observatory filters
 */
export function proposeTargetFilter(code, targetOptions) {
  if (!code || !Array.isArray(targetOptions) || targetOptions.length === 0) return '';
  const byNorm = new Map();
  for (const opt of targetOptions) {
    const n = normalize(opt.value);
    if (!byNorm.has(n)) byNorm.set(n, opt.value);
  }
  const c = normalize(code);

  // 1. Direct normalized match (R↔r, Ha↔HA, O3↔o3, etc.)
  if (byNorm.has(c)) return byNorm.get(c);

  // 2. Alias-group match (Ha↔Halpha, L↔Lum, SR↔SloanR, ...)
  for (const group of ALIAS_GROUPS) {
    if (group.includes(c)) {
      for (const alias of group) {
        if (byNorm.has(alias)) return byNorm.get(alias);
      }
    }
  }

  // 3. No confident match — let the user choose.
  return '';
}

/** Distinct, order-preserving list of filter codes used across imported targets. */
export function collectFilterCodes(targets) {
  const seen = new Set();
  const codes = [];
  for (const t of targets || []) {
    for (const ec of t.exposureConfigs || []) {
      const f = ec.filter;
      if (f && !seen.has(f)) {
        seen.add(f);
        codes.push(f);
      }
    }
  }
  return codes;
}
